/**
 * <paragraph-snap-rail>
 *
 * Wraps a horizontal scroll-snap rail + its pagination dots.
 * On mobile (viewport ≤720px), each direct child inside the inner
 * flex row is one full-viewport snap card; this element watches which
 * card is centered and flips the matching dot to [data-active="true"].
 *
 * Expected DOM (inner track may be named __grid OR __row):
 *   <paragraph-snap-rail>
 *     <div class="...__grid" role="list">   (or "...__row")
 *       <article class="..." role="listitem">…</article>
 *       <article class="..." role="listitem">…</article>
 *     </div>
 *     <div class="...__dots" aria-hidden="true">
 *       <span data-active="true"></span>
 *       <span></span>
 *     </div>
 *   </paragraph-snap-rail>
 *
 * Noop on desktop where the rail is a static grid. Respects
 * prefers-reduced-motion by not fighting scroll behavior.
 */
(function () {
  if (customElements.get('paragraph-snap-rail')) return;

  class ParagraphSnapRail extends HTMLElement {
    connectedCallback() {
      // Match the inner scroll-snap track regardless of its BEM element name.
      // pw-pairings uses `__grid`, pw-gallery uses `__row`; both are valid
      // rails. Falling back to the first role="list" keeps this resilient to
      // future consumers that name the track something else.
      const rail =
        this.querySelector('[class$="__grid"], [class$="__row"]') ||
        this.querySelector('[role="list"]');
      const dots = this.querySelectorAll('[class$="__dots"] span');
      if (!rail || dots.length === 0) return;

      const cells = Array.from(rail.children);
      if (cells.length === 0) return;

      // IntersectionObserver against the rail container; fires when a cell
      // is at least 60% visible inside the rail viewport.
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const idx = cells.indexOf(entry.target);
            if (idx < 0) return;
            dots.forEach((d, i) => {
              if (i === idx) d.setAttribute('data-active', 'true');
              else d.removeAttribute('data-active');
            });
          });
        },
        { root: rail, threshold: 0.6 }
      );

      cells.forEach((c) => io.observe(c));
    }
  }

  customElements.define('paragraph-snap-rail', ParagraphSnapRail);
})();
